import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownOutlined, InfoCircleOutlined, SearchOutlined, ShoppingCartOutlined, UpOutlined } from '@ant-design/icons';
import { formatFinanceCurrency } from '../pendingInvoiceCost';
import { ReportLongTable, ReportSection } from '../ReportTab';
import type { IncomeStatementPurchase } from './incomeStatementApi';
import { monthShortLabel } from './incomeStatement';
import { PurchaseRowCostGap, purchaseRowSourceLabel } from './purchaseRowLabels';

// „Zakupy w okresie (nie obniżają wyniku)” under the Rachunek wyników table
// (profit at sale, docs/decisions/2026-10-29-zysk-przy-sprzedazy.md): the ZD
// purchases of the period as information only — they never enter a line.
// Report anatomy (modules/reports/DESIGN.md): ReportSection, one-line lead,
// a fixed-layout `report-table`, text cut with the full text in `title`.

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const S = 'reports:profitability.incomeStatement.purchaseSources';

interface Props {
  rows: IncomeStatementPurchase[];
  language: string;
  t: TranslateFn;
}

function rowKey(row: IncomeStatementPurchase): string {
  return `${row.kind}-${row.supplierOrderId}-${row.invoiceId || 'plan'}-${row.month}`;
}

/**
 * Search of the list (2026-10-30: a year has ~2 000 rows on staging): the ZD
 * number, the supplier, the invoice number or the source ZK of a draft.
 */
export function filterPurchaseRows(rows: IncomeStatementPurchase[], query: string): IncomeStatementPurchase[] {
  const needle = query.trim().toLocaleLowerCase('pl');
  if (!needle) return rows;
  return rows.filter((row) => [row.orderNumber, row.supplierName, row.documentNumber, row.draft?.sourceOrderNumber]
    .some((value) => typeof value === 'string' && value.toLocaleLowerCase('pl').includes(needle)));
}

const PurchasesInPeriodSection: React.FC<Props> = ({ rows, language, t }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const shown = useMemo(() => filterPurchaseRows(rows, query), [rows, query]);
  if (rows.length === 0) return null;

  const contractText = (row: IncomeStatementPurchase) => {
    if (row.draft) return '—';
    const amount = formatFinanceCurrency(row.contractNet, row.contractCurrency);
    return row.contractCurrency === 'PLN'
      ? amount
      : `${amount} · ${t(`${S}.rate`)} ${row.contractRate ?? '—'} · ${formatFinanceCurrency(row.contractNetPLN)}`;
  };
  const invoiceText = (row: IncomeStatementPurchase) => {
    if (!row.invoiceId) return t(`${S}.plan`);
    const kind = row.kind === 'CORRECTION' || row.kind === 'WITHDRAWAL' ? ` · ${t(`${S}.kinds.${row.kind}`)}` : '';
    const rate = row.invoiceCurrency && row.invoiceCurrency !== 'PLN' ? ` · ${t(`${S}.rate`)} ${row.invoiceRate ?? '—'}` : '';
    return `${row.documentNumber ?? ''}${kind}${rate}`;
  };

  const columns: ColumnsType<IncomeStatementPurchase> = [
    {
      title: t(`${S}.order`),
      key: 'order',
      render: (_, row) => {
        const source = purchaseRowSourceLabel(row, t);
        return (
          <span className="report-cut" title={`${row.orderNumber} · ${source}`}>
            <Link to={`/app/supplier-orders/${row.supplierOrderId}`}>{row.orderNumber}</Link>
            <span className="report-muted"> · {source}</span>
          </span>
        );
      },
    },
    { title: t(`${S}.month`), key: 'month', width: 88, render: (_, row) => monthShortLabel(row.month, language) },
    {
      title: t(`${S}.contract`),
      key: 'contract',
      width: 180,
      render: (_, row) => <span className="report-cut report-num" title={contractText(row)}>{contractText(row)}</span>,
    },
    {
      title: t(`${S}.invoice`),
      key: 'invoice',
      width: 200,
      render: (_, row) => (
        <span className="report-cut" title={invoiceText(row)}>
          {row.invoiceId ? <Link to={`/app/purchase-invoices/${row.invoiceId}`}>{invoiceText(row)}</Link> : invoiceText(row)}
        </span>
      ),
    },
    {
      title: t(`${S}.net`),
      key: 'net',
      width: 150,
      align: 'right',
      render: (_, row) => (
        <span className="report-num">
          {row.netPLN == null ? '—' : formatFinanceCurrency(row.netPLN)}
          <PurchaseRowCostGap row={row} t={t} />
        </span>
      ),
    },
    {
      title: <span title={t(`${S}.extra`)}>{t(`${S}.extraShort`)}</span>,
      key: 'extra',
      width: 150,
      align: 'right',
      render: (_, row) => <span className="report-num">{formatFinanceCurrency(row.extraCostPLN)}</span>,
    },
  ];

  const totalNet = rows.reduce((sum, row) => sum + (row.netPLN ?? 0), 0);
  const totalExtra = rows.reduce((sum, row) => sum + (row.extraCostPLN ?? 0), 0);
  // „Razem” of the table sums the rows it lists (all of them without a search).
  const shownNet = shown.reduce((sum, row) => sum + (row.netPLN ?? 0), 0);
  const shownExtra = shown.reduce((sum, row) => sum + (row.extraCostPLN ?? 0), 0);

  return (
    <ReportSection
      title={t(`${S}.title`)}
      icon={<ShoppingCartOutlined />}
      tone="gray"
      lead={t(`${S}.lead`)}
      className="income-statement-purchases"
      actions={(
        <>
          {/* The same sum as „Zakupy u dostawców” in „Prognoza a wynik” (with extra costs). */}
          <span className="report-muted report-num" data-testid="income-statement-purchases-total">
            {t(`${S}.headerTotal`, { count: rows.length, amount: formatFinanceCurrency(totalNet + totalExtra) })}
          </span>
          <Tooltip title={t(`${S}.hint`)}>
            <InfoCircleOutlined className="report-muted" aria-label={t(`${S}.hint`)} />
          </Tooltip>
          <Button
            size="small"
            icon={open ? <UpOutlined /> : <DownOutlined />}
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            data-testid="income-statement-purchases-toggle"
          >
            {open ? t(`${S}.hide`) : t(`${S}.show`)}
          </Button>
        </>
      )}
    >
      {open && (
        <>
          <div className="report-controls">
            <Input
              prefix={<SearchOutlined />}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t(`${S}.searchPlaceholder`)}
              aria-label={t(`${S}.searchPlaceholder`)}
              allowClear
              className="income-statement-purchases__search"
              data-testid="income-statement-purchases-search"
            />
            {query.trim() && (
              <span className="report-controls__counter">{t(`${S}.searchCount`, { count: shown.length, total: rows.length })}</span>
            )}
          </div>
          <ReportLongTable rowCount={shown.length} testId="income-statement-purchases-rows">
            <div className="report-table" data-testid="income-statement-purchases">
              <Table<IncomeStatementPurchase>
                rowKey={rowKey}
                size="small"
                tableLayout="fixed"
                columns={columns}
                dataSource={shown}
                pagination={shown.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
                locale={{ emptyText: t(`${S}.searchEmpty`) }}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={4}>{t(`${S}.total`)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right"><span className="report-num">{formatFinanceCurrency(shownNet)}</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right"><span className="report-num">{formatFinanceCurrency(shownExtra)}</span></Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </div>
          </ReportLongTable>
        </>
      )}
    </ReportSection>
  );
};

export default PurchasesInPeriodSection;
